import { useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';

import Toolbar from 'components/common/Toolbar/Toolbar';
import TabList from 'components/common/TabList/TabList';
import Tab from 'components/common/Tab/Tab';
import Tabs from 'components/common/Tabs/Tabs';
import TabContent from 'components/common/TabContent/TabContent';
import TabContents from 'components/common/TabContents/TabContents';
import { DataCardProps } from 'plugins/types';

import { dataDetailsCardTriggerState } from 'components/common/Managers/ViewStateManager.state';
import ActionsButton from '../actions/sentinel-one/ActionsButton';
import EndpointCardSummaryHeader from '../endpoint-card-summary-header/EndpointCardSummaryHeader';
import EndpointDetailsHeader from './EndpointDetailsHeader';
import DVEndpointDetailsRendererApplication from './DVEndpointDetailsApplication';
import EndpointCloudProvidersTab from './endpoint-cloud-provider/EndpointCloudProviderTab';
import DVEndpointDetailsRendererGeneral from './DVEndpointDetailsGeneral';
import DVEndpointDetailsRendererTasks from './DVEndpointDetailsTasks';
import { EndpointTabs } from './DVEndpointDetailsRenderer.consts';
import { isCloudProvidersVisible } from './endpoint-cloud-provider/utils';
import DVEndpointDetailsRendererTags from './endpoint-tags/DVEndpointDetailsTags';

import './DVEndpointDetailsRenderer.scss';

/**
 * This component displays endpoint details for a Deep Visibility event.
 *
 * @prop onClose               function that closes the view
 * @prop logEntry              the currently selected log line whose field/value pairs are shown
 * @prop continuationToken     unique identifier representing the currently selected log line
 * @prop initialData           endpoint data needed for the tabs and actions
 */
export const DVEndpointDetailsRenderer = ({ onClose, onBack, values, initialData }: DataCardProps): JSX.Element => {
  const [activeTab, setActiveTab] = useState(EndpointTabs.general);
  const setDataDetailsFieldTrigger = useSetRecoilState(dataDetailsCardTriggerState);

  const toolbarItems = [<ActionsButton values={values} initialData={initialData} />];
  const endpointData = initialData?.s1Endpoint;

  // reset state on unmount so we don't show it unbidden
  useEffect(() => () => setDataDetailsFieldTrigger(null), []);

  const endpointTabList = Object.values(EndpointTabs)
    .filter((tab) => tab !== EndpointTabs.cloud || isCloudProvidersVisible(endpointData))
    .map((tab) => (
      <Tab key={tab} eventKey={tab}>
        {tab}
      </Tab>
    ));

  const handleTabSelect = (eventKey: string) => {
    setActiveTab(eventKey as EndpointTabs);
  };

  return (
    <>
      <div className="DVEndpointDetailsRenderer__Header">
        <EndpointDetailsHeader onClose={onClose} onBack={onBack} />
      </div>
      <div className="DVEndpointDetailsRenderer__Summary">
        {endpointData && <EndpointCardSummaryHeader endpointData={endpointData} />}
        <Toolbar className="DVEndpointDetailsRenderer__Summary__Actions" items={toolbarItems} />
      </div>
      <div className="DVEndpointDetailsRenderer__Content">
        <Tabs activeTab={activeTab} onSelect={handleTabSelect} mountOnEnter={true}>
          <TabList className="EndpointDetails__Tabs">{endpointTabList}</TabList>
          <TabContents>
            <TabContent eventKey={EndpointTabs.general}>
              <div className="EndpointDetails__TabContent">
                <DVEndpointDetailsRendererGeneral initialData={initialData} />
              </div>
            </TabContent>
            <TabContent eventKey={EndpointTabs.cloud}>
              <div className="EndpointDetails__TabContent">
                <EndpointCloudProvidersTab cloudProvidersData={endpointData?.cloudProviders || []} />
              </div>
            </TabContent>
            <TabContent eventKey={EndpointTabs.appInventory}>
              <div className="EndpointDetails__TabContent AppInventory__Content">
                <DVEndpointDetailsRendererApplication initialData={initialData} />
              </div>
            </TabContent>
            <TabContent eventKey={EndpointTabs.tasks}>
              <div className="EndpointDetails__TabContent Tasks__Content">
                <DVEndpointDetailsRendererTasks initialData={initialData} />
              </div>
            </TabContent>
            <TabContent eventKey={EndpointTabs.tags}>
              <div className="EndpointDetails__TabContent Tags__Content">
                <DVEndpointDetailsRendererTags initialData={initialData} values={values} />
              </div>
            </TabContent>
          </TabContents>
        </Tabs>
      </div>
    </>
  );
};
